import { z } from 'zod';

import { UserGender, UserOrderBy, UserStatus } from '../enums';
import { ApiRoleDto } from './role';
import { CountriesEnum } from './countries';
import {
  TrainerOnboardingAddressValidationDto,
  TrainerOnboardingContactValidationDto,
  TrainerOnboardingSocialMediaValidationDto,
} from './onboarding';
import { AboutTranslation } from './aboutTranslation';
import { CivilDateDto, CivilTimeDto } from './civilDate';
import { onlyEligibleAutoRenew } from './membership';
import {
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  USERNAME_PATTERN,
} from '../utils/username';

/** Lower-cased on the way in; the stored spelling is always lower case. */
export const UsernameDto = z
  .string()
  .trim()
  .toLowerCase()
  .min(USERNAME_MIN_LENGTH, 'VALIDATION__Min3CharLimit')
  .max(USERNAME_MAX_LENGTH, 'VALIDATION__Max30CharLimit')
  .regex(USERNAME_PATTERN, 'VALIDATION__InvalidUsername');

export const UsernameAvailabilityQueryDto = z
  .object({ username: UsernameDto })
  .strict();

const SocialMediaUrlTypeDto = z.enum(
  ['FACEBOOK', 'INSTAGRAM', 'YOU_TUBE', 'LINKED_IN', 'TIK_TOK'],
  {
    message: 'VALIDATION__InvalidSocialMediaType',
    invalid_type_error: 'invalidSocialMediaType',
  },
);

export type SocialMediaUrlType = z.infer<typeof SocialMediaUrlTypeDto>;

const SocialMediaUrlDto = z
  .array(
    z.object({
      type: SocialMediaUrlTypeDto,
      url: z.string(),
    }),
  )
  .optional();

export const CreateUserDto = z
  .object({
    email: z.string().trim().email(),
    googleIdentityProviderId: z.string().uuid(),
    name: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit'),
    tenantId: z.string().uuid(),
  })
  .strict();

export type ApiCreateUserDto = z.infer<typeof CreateUserDto>;

export const UserStatusEnum = z.enum([
  UserStatus.ACTIVE,
  UserStatus.PENDING,
  UserStatus.ARCHIVED,
  UserStatus.REJECTED,
  UserStatus.DELETED,
]);

export type UserStatusDto = z.infer<typeof UserStatusEnum>;

export const OrganizationClientOrderByEnum = z.enum([
  UserOrderBy.CONNECTED_AT,
  UserOrderBy.MEMBERSHIP_RENEWAL,
  UserOrderBy.NAME,
]);

export type OrganizationClientOrderByDTO = z.infer<
  typeof OrganizationClientOrderByEnum
>;

export const FindUserDto = z
  .object({
    id: z.string().uuid().optional(),
    email: z.string().trim().email().optional(),
    googleIdentityProviderId: z.string().uuid().optional(),
    status: z.string().toUpperCase().pipe(z.nativeEnum(UserStatus)),
    tenantId: z.string().uuid().optional(),
    username: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
  })
  .strict();

export type ApiFindUserDto = z.infer<typeof FindUserDto>;

export const UserDto = CreateUserDto.extend({
  username: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit'),
  status: z.string().toUpperCase().pipe(z.nativeEnum(UserStatus)),
}).strict();

/**
 * THE password policy, as one object rather than as a rule repeated per route.
 *
 * Every way an account acquires a password has to answer to the same schema:
 * self-registration, an accepted invite, and the reset link. `PATCH
 * auth/reset-password` did not — it read `@Body('password')` with no schema at
 * all, so the only limit on the password a reset left behind was whatever
 * Firebase happened to enforce. Tightening the rules here now tightens them
 * everywhere, which is the property a copied `z.string().min(3)` cannot have.
 *
 * The minimum may never sit below the Identity Platform policy (stage: ENFORCE,
 * min 8). It did — min 3 — and stage refused a password the form had approved,
 * which reached the user only as "Account creation failed".
 */
export const PasswordDto = z
  .string()
  .min(8, 'VALIDATION__Min8CharLimit')
  .max(255, 'VALIDATION__Max255CharLimit');

/**
 * Signing in checks a password that already exists, so it must not answer to
 * the policy above: the policy does not force an upgrade on sign-in, and older
 * accounts (DEV, imported V1 users) keep shorter passwords.
 */
const LoginPasswordDto = z
  .string()
  .min(3, 'VALIDATION__Min3CharLimit')
  .max(255, 'VALIDATION__Max255CharLimit');

const AccountDto = z
  .object({
    email: z.string().trim().email('VALIDATION__InvalidEmail'),
    name: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit'),
    // Optional: omitted -> the API derives a free one from `name`.
    username: UsernameDto.optional(),
    password: PasswordDto,
    confirmPassword: PasswordDto,
    privacyPolicy: z.boolean().refine((val) => val === true, {
      message: 'VALIDATION__Required',
    }),
  })
  .strict();
/**
 * Self creation flow
 *
 * `reactivate` drives the deleted-account branch:
 *  - undefined -> caller hasn't chosen yet; the API reports the choice back
 *  - true      -> reactivate the previously deleted account
 *  - false     -> ignore the old account and create a fresh one
 */
export const CreateAccountDto = AccountDto.extend({
  reactivate: z.boolean().optional(),
}).refine(
  ({ confirmPassword, password }) => {
    return confirmPassword === password;
  },
  {
    message: 'VALIDATION__ConfirmationPasswordMustMatch',
    path: ['confirmPassword'],
  },
);

export const LoginAccountDto = AccountDto.pick({
  email: true,
}).extend({
  password: LoginPasswordDto,
  rememberMe: z.boolean().optional(),
});

export const ForgotPasswordDto = AccountDto.pick({ email: true });

export const SetupPasswordDto = AccountDto.pick({
  password: true,
  confirmPassword: true,
})
  .extend({
    token: z.string({ message: 'VALIDATION__SetupPasswordTokenInvalid' }),
  })
  .refine(
    ({ confirmPassword, password }) => {
      return confirmPassword === password;
    },
    {
      message: 'VALIDATION__ConfirmationPasswordMustMatch',
      path: ['confirmPassword'],
    },
  );

/**
 * The body of `PATCH auth/reset-password`, whose single-use token travels in
 * the query string. Deliberately NOT `SetupPasswordDto` above: that one also
 * demands `confirmPassword` and a body `token`, neither of which this route has
 * ever been sent — the confirmation is compared in the browser and the token is
 * a query parameter.
 */
export const ResetPasswordDto = z.object({ password: PasswordDto }).strict();

/**
 * The `?hash=` an invite link carries: the primary key of a `UserTokens` row,
 * so a UUID and nothing else.
 *
 * Untyped, it went to `findToken({ id: hash })` as `hash as string` — and a
 * request without one reached Postgres as `id = undefined`, which is a driver
 * error, i.e. a 500 (and a Sentry issue) for a plainly malformed call to a
 * PUBLIC route. `.strict()` because both routes take exactly this one
 * parameter.
 */
export const InviteHashDto = z.object({ hash: z.string().uuid() }).strict();

export type ApiCreateAccountDto = z.infer<typeof CreateAccountDto>;
export type ApiLoginAccountDto = z.infer<typeof LoginAccountDto>;
export type ApiForgotPasswordDto = z.infer<typeof ForgotPasswordDto>;
export type ApiSetupPasswordDto = z.infer<typeof SetupPasswordDto>;
export type ApiResetPasswordDto = z.infer<typeof ResetPasswordDto>;
export type ApiInviteHashDto = z.infer<typeof InviteHashDto>;

export const OrganizationUserInviteDto = z
  .object({
    email: z.string().trim().email(),
    membershipId: z.string().uuid().optional(),
    // Mirrors AssignClientMembershipDto: only meaningful alongside
    // membershipId. Defaults keep every pre-existing invite (no type sent)
    // behaving exactly as before — a plain SINGLE-shaped attach.
    membershipType: z.enum(['SINGLE', 'RECURRING']).optional(),
    requiresCardLinkNow: z.boolean().optional(),
    // Task 2 (membership-renewal plan) — same rule as AssignClientMembershipDto:
    // only meaningful alongside a RECURRING, cardless membership rider.
    autoRenew: z.boolean().optional(),
    roleIds: z.array(z.string().uuid()),
    trainerIds: z.array(z.string().uuid()).optional(),
    userType: z.enum(['OWNER', 'TRAINER', 'CLIENT', 'STAFF']),
    questionnaireIds: z.array(z.string().uuid()).optional(),
  })
  // the assign DTOs' own rule, on the invite's field names (both flags are
  // optional here; absent means SINGLE / no card, as the rider applies them)
  .refine(
    (data) =>
      onlyEligibleAutoRenew({
        autoRenew: data.autoRenew,
        requiresCardLinkNow: data.requiresCardLinkNow ?? false,
        type: data.membershipType ?? 'SINGLE',
      }),
    {
      message: 'VALIDATION__AutoRenewOnlyForCardlessRecurring',
      path: ['autoRenew'],
    },
  );

export type ApiOrganizationUserInviteDto = z.infer<
  typeof OrganizationUserInviteDto
>;

export const AcceptInviteDto = z.object({
  privacyPolicy: z.boolean().refine((val) => val === true, {
    message: 'VALIDATION__Required',
  }),
  // Required only when the invite carries a RECURRING membership — the
  // server checks it matches that membership's currently effective Terms of
  // Use version before activating it (see `messages.membershipTermsOfUseVersionMismatch`).
  touVersionId: z.string().uuid().optional(),
  // 6.10 — required when the organization asks clients to accept its terms on
  // connecting; the server checks it is the org default version in force.
  connectionTouVersionId: z.string().uuid().optional(),
});

export type ApiAcceptInviteDto = z.infer<typeof AcceptInviteDto>;

export const UpdateInviteDto = z.object({
  type: z.enum(['accept', 'reject']),
  touVersionId: z.string().uuid().optional(),
  connectionTouVersionId: z.string().uuid().optional(),
});
export type ApiUpdateInviteDto = z.infer<typeof UpdateInviteDto>;

// F10 — org disconnect of a client: does the client KEEP access to content
// (workout/program) already assigned to them via Schedule? Defaults to true
// (unchanged behavior) so callers that omit the body entirely (no
// Content-Type → req.body is `undefined`, not `{}`) still validate cleanly.
export const DisconnectClientDto = z.preprocess(
  (value) => value ?? {},
  z
    .object({
      allowContentAccess: z.boolean().default(true),
    })
    .strict(),
);
export type ApiDisconnectClientDto = z.infer<typeof DisconnectClientDto>;

export const UpdateUserDto = z
  .object({
    roleIds: z.array(z.string().uuid()).optional(),
    name: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    userSettings: z
      .object({
        profileUrl: z.string().nullable().optional(),
      })
      .optional(),
    status: z.enum([UserStatus.ACTIVE, UserStatus.ARCHIVED]).optional(),
    address: TrainerOnboardingAddressValidationDto.optional(),
    contact: TrainerOnboardingContactValidationDto.optional(),
    socialMedia: TrainerOnboardingSocialMediaValidationDto.optional(),
  })
  .strict();

export type ApiUpdateUserDto = z.infer<typeof UpdateUserDto>;

// F3 — profile switcher context persistence (Users.lastUsedContext).
// 'personal' = org-less "personal profile" mode, 'organization' = a regular
// organization is the active context.
export const UserContextEnum = z.enum(['personal', 'organization']);

export type UserContextDto = z.infer<typeof UserContextEnum>;

export const SetPreferredOrganizationDto = z
  .object({
    organizationId: z.string().uuid().optional(),
    context: UserContextEnum.optional(),
  })
  .strict();

export type ApiSetPreferredOrganizationDto = z.infer<
  typeof SetPreferredOrganizationDto
>;

export const MyProfileUpdateDto = z
  .object({
    username: UsernameDto.optional(),
    name: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    email: z.string().trim().email().optional(),
    address: TrainerOnboardingAddressValidationDto.optional(),
    contact: TrainerOnboardingContactValidationDto.optional(),
    socialMedia: TrainerOnboardingSocialMediaValidationDto.optional(),
    fitnessProfile: z
      .object({
        about: z.array(AboutTranslation).optional(),
        weight: z.number().optional(),
        height: z.number().optional(),
        fitnessLevel: z.number().optional(),
        bodyWeigh: z.number().optional(),
        bmi: z.number().optional(),
        muscleMass: z.number().optional(),
        muscleMassPercentage: z.number().optional(),
        bodyFat: z.number().optional(),
        bodyFatPercentage: z.number().optional(),
        hearthRate: z.number().optional(),
        maxHearthRate: z.number().optional(),
        fitnessGoals: z.array(z.string()).optional(),
        healthIssues: z.array(z.string()).optional(),
      })
      .optional(),
    userSettings: z
      .object({
        timezone: z.string().optional(),
        locale: z.string().optional(),
        gender: z.nativeEnum(UserGender).optional(),
        about: z.array(AboutTranslation).optional(),
        dateOfBirth: CivilDateDto.optional(),
        rememberMe: z.boolean().optional(),
        profileUrl: z.string().nullable().optional(),
        isPublic: z.boolean().optional(),
        height: z.enum(['cm', 'mm', 'ft']).optional().nullable(),
        weight: z.enum(['kg', 'lb', 'oz']).optional().nullable(),
        energy: z.enum(['kcal', 'kj']).optional().nullable(),
        volume: z.enum(['l', 'oz']).optional().nullable(),
        length: z.enum(['km', 'mi']).optional().nullable(),
        bodyMassIndex: z.enum(['kg', 'psf']).optional().nullable(),
        dailyActivityReminderMorningTime: CivilTimeDto.optional().nullable(),
        dailyActivityReminderEveningTime: CivilTimeDto.optional().nullable(),
      })
      .optional(),
    // Temelj 4 (D4): no password fields. This endpoint changed the password
    // without the old one, so a stolen session could take the account over;
    // `.strict()` now answers any of them with 422. A future settings screen
    // must reauthenticate with Firebase and check a recent `auth_time`.
  })
  .strict();

export type ApiMyProfileUpdateDto = z.infer<typeof MyProfileUpdateDto>;

/**
 * FE ONLY
 */
export type ApiUserDto = z.infer<typeof UserDto> & {
  roles: ApiRoleDto[];
};

export const PhoneNumberOnboardingStepDto = z.object({
  phoneNumber: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit'),
  countryCode: CountriesEnum,
  isPhoneNumberPublic: z.boolean().optional(),
});

export const SocialMediaOnboardingStepDto = z.object({
  socialMedia: SocialMediaUrlDto.optional(),
});

// Front-desk "who's due" filter (spec: 7/3/1 days out, due today, or already
// expired) — exact checkpoints matching the renewal-reminder cadence, not a
// cumulative window: picking '7' means "due in exactly 7 days", so a staff
// member calling that checkpoint's list doesn't also see the '3'/'1' rows.
export const MembershipDueBucketEnum = z.enum([
  '7',
  '3',
  '1',
  'today',
  'expired',
]);
export type ApiMembershipDueBucket = z.infer<typeof MembershipDueBucketEnum>;

export const OrganizationClientFilterDto = z.object({
  membershipIds: z.array(z.string().uuid()).optional(),
  groupIds: z.array(z.string().uuid()).optional(),
  trainerIds: z.array(z.string().uuid()).optional(),
  withoutTrainer: z.boolean().optional(),
  statuses: z.array(UserStatusEnum).optional(),
  dueBuckets: z.array(MembershipDueBucketEnum).optional(),
  // "Not paid" filter — the client's current membership cycle is a renewal
  // (`renewalOrigin` set) whose payment state is UNPAID or PARTIAL.
  notPaid: z.boolean().optional(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  orderBy: OrganizationClientOrderByEnum.default(UserOrderBy.NAME),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  search: z.string().trim().max(255).optional(),
  offset: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
});

export const OrganizationUserQueryDto = z.object({
  search: z.string().trim().max(255).optional(),
  offset: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  orderBy: z.string().min(1).max(255).default('name'),
  orderDirection: z.string().min(1).max(4).default('ASC'),
  status: z.string().optional(),
  roles: z.string().optional(),
});

export type ApiOrganizationUserQueryDto = z.infer<
  typeof OrganizationUserQueryDto
>;

export type ApiOrganizationClientFilterDto = z.infer<
  typeof OrganizationClientFilterDto
>;
